import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useContext } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form, { FormProps } from "shared/components/form/Form";
import { FormUnsavedIndicator } from "shared/components/form/FormUnsavedIndicator";

export interface FormDrawerProps<T extends FieldValues, R> {
  title: string;
  subtitle?: string;
  formProps: FormProps<T, R>;
  bodyProps?: Record<string, string | boolean | number>;
  onCloseComplete: () => void;
}

export const FormDrawer = <T extends FieldValues, R>({
  onCloseComplete,
  title,
  subtitle,
  formProps,
  bodyProps,
  children,
}: React.PropsWithChildren<FormDrawerProps<T, R>>) => {
  const { t } = useTranslation(["investorEntity", "common"]);

  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });

  const initialFocusRef = React.useRef<HTMLInputElement>(null);

  const onSuccess = async (data: R, values: T) => {
    onClose();
    return formProps.onSuccess?.(data, values);
  };
  return (
    <Drawer
      size="editor"
      variant="floating"
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      closeOnEsc={true}
      closeOnOverlayClick={true}
      initialFocusRef={initialFocusRef}
    >
      <Form {...formProps} onSuccess={onSuccess}>
        <FormDrawerContext.Provider value={{ initialFocusRef }}>
          <DrawerOverlay background="drawerOverlay" />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>
              <Text>{title}</Text>
              {!!subtitle && (
                <Text fontWeight="400" fontSize="12px" textColor="gray.600">
                  {subtitle}
                </Text>
              )}
            </DrawerHeader>
            <DrawerBody {...bodyProps}>
              <QueryBoundary>{children}</QueryBoundary>
            </DrawerBody>
            <DrawerFooter justifyContent="space-between">
              <FormUnsavedIndicator />
              <HStack gap={2}>
                <FormCancelButton onClick={onClose} />
                <FormSubmitButton>{t("common:saveEdits")}</FormSubmitButton>
              </HStack>
            </DrawerFooter>
          </DrawerContent>
        </FormDrawerContext.Provider>
      </Form>
    </Drawer>
  );
};

const FormDrawerContext = React.createContext<{
  initialFocusRef: React.RefObject<HTMLInputElement> | null;
}>({
  initialFocusRef: null,
});

export const useFormDrawerInitialFocusRef = () => {
  const { initialFocusRef } = useContext(FormDrawerContext);
  return initialFocusRef;
};
